import { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { useChronometry, useRegList, useRepeat } from '@/api/m3';
import type { RegList, RegRow, Totals } from '@/api/m3-types';
import { Button } from '@/components/kit/Button';
import { TextLink } from '@/components/kit/Controls';
import { DateRangePicker } from '@/components/kit/DateRangePicker';
import { FilterBar, FilterChip, SearchInput } from '@/components/kit/Filters';
import { PageHeader, Segments } from '@/components/kit/Layout';
import { CHECKLIST_LEGEND, Legend, ProgressBar, StatusDot } from '@/components/kit/Marks';
import { MetricRow } from '@/components/kit/MetricRow';
import { EmptyState, ErrorState, Note, TableSkeleton } from '@/components/kit/Note';
import { Panel } from '@/components/kit/Panel';
import { Pill } from '@/components/kit/Pill';
import { Na } from '@/components/kit/Table';
import { money, mmss, date as ruDate } from '@/lib/format';
import { useDebouncedSearch } from '@/features/base/shared/useDebouncedSearch';
import { useListParams } from '@/features/base/shared/useListParams';
import { CHECKLIST_COLUMNS } from '@/features/crm/deals/DealPage';
import { nums, READINESS, REG_STATUS } from '@/features/crm/labels';
import { useM3Action } from '@/features/crm/useM3Submit';
import { RefundModal, type RefundTarget } from './RegDialogs';

type Tab = 'active' | 'reserve' | 'declined' | 'moved' | 'chrono' | 'rep';

const TABS: { value: Tab; label: string }[] = [
  { value: 'active', label: 'Номера' },
  { value: 'reserve', label: 'Резерв' },
  { value: 'declined', label: 'Отказы' },
  { value: 'moved', label: 'Переносы' },
  { value: 'chrono', label: 'Хронометраж' },
  { value: 'rep', label: 'Повторное участие' },
];

const PAY = [
  { value: 'none', label: 'Не оплачены' },
  { value: 'partial', label: 'Частично' },
  { value: 'full', label: 'Полностью' },
];

/**
 * Регистрации (viewRegs в макете, БЛ 11.4): реестр номеров группами
 * «мероприятие → номинация» с подытогами и местами, резерв, отказы,
 * переносы, хронометраж и «Повторное участие».
 */
export function RegistrationsPage() {
  const navigate = useNavigate();
  const { values, set } = useListParams(['tab', 'eventId', 'nomination', 'pay', 'readiness', 'owner', 'from', 'to', 'q'] as const);
  const tab = (values.tab as Tab | null) ?? 'active';
  const registry = tab !== 'chrono' && tab !== 'rep';
  const [text, setText] = useDebouncedSearch(values.q, (q) => set({ q }));
  const [refund, setRefund] = useState<RefundTarget | null>(null);

  const list = useRegList(
    registry
      ? {
          tab,
          eventId: values.eventId,
          ...(tab === 'active'
            ? { nomination: values.nomination, pay: values.pay, readiness: values.readiness, owner: values.owner, from: values.from, to: values.to, q: values.q }
            : {}),
        }
      : { tab: 'active' },
  );
  const active = useRegList({ tab: 'active' });
  const data = list.data;
  // Отчёты мероприятия — без «Все»: по умолчанию первое мероприятие с номерами
  const reportEvent = values.eventId ?? active.data?.events[0]?.id ?? null;

  const segs = registry ? (
    <Segments
      value={values.eventId ?? 'all'}
      onChange={(id) => set({ eventId: id === 'all' ? null : id })}
      items={[{ value: 'all', label: 'Все', count: data?.all }, ...(data?.events ?? []).map((e) => ({ value: e.id, label: e.label, count: e.count }))]}
    />
  ) : (
    <Segments
      value={reportEvent ?? ''}
      onChange={(id) => set({ eventId: id })}
      items={(active.data?.events ?? []).map((e) => ({ value: e.id, label: e.label, count: e.count }))}
    />
  );

  return (
    <div className="main">
      <PageHeader title="Регистрации" count={active.data?.total.teams}>
        <Button tone="primary" onClick={() => navigate('/registrations/new')}>
          ＋ Регистрация
        </Button>
      </PageHeader>
      <div className="tabs">
        {TABS.map((t) => (
          <div
            key={t.value}
            className={t.value === tab ? 'tab on' : 'tab'}
            onClick={() => set({ tab: t.value === 'active' ? null : t.value, nomination: null, pay: null, readiness: null, owner: null, from: null, to: null, q: null })}
          >
            {t.label}
          </div>
        ))}
      </div>
      {segs}

      {tab === 'active' ? (
        <>
          <FilterBar search={<SearchInput value={text} onChange={setText} placeholder="Команда, руководитель, телефон" />}>
            <FilterChip
              label="Номинация"
              value={values.nomination}
              onChange={(nomination) => set({ nomination })}
              options={(data?.facets.nominations ?? []).map((f) => ({ value: f.value, label: f.label, side: f.count }))}
            />
            <FilterChip label="Оплата" value={values.pay} onChange={(pay) => set({ pay })} options={PAY} />
            <FilterChip
              label="Готовность"
              value={values.readiness}
              onChange={(readiness) => set({ readiness })}
              options={(data?.facets.readiness ?? []).map((f) => ({ value: f.value, label: f.label, side: f.count }))}
            />
            <FilterChip
              label="Ответственный"
              value={values.owner}
              onChange={(owner) => set({ owner })}
              options={(data?.facets.owners ?? []).map((f) => ({ value: f.value, label: f.label }))}
            />
            <DateRangePicker label="Зарегистрирован" from={values.from} to={values.to} onChange={(from, to) => set({ from, to })} />
          </FilterBar>
          {data ? (
            <MetricRow
              variant="slice"
              items={[
                { label: 'Команд', value: data.metrics.teams },
                { label: 'Участников', value: data.metrics.participants },
                { label: 'План', value: money(data.metrics.plan), sub: 'начислено' },
                { label: 'Факт', value: money(data.metrics.fact) },
                { label: 'Долг', value: money(data.metrics.debt) },
                { label: 'Допущено', value: data.metrics.admitted, sub: `из ${data.metrics.teams}` },
              ]}
            />
          ) : null}
        </>
      ) : null}

      {registry ? (
        <Panel flush>
          {list.error ? (
            <ErrorState title="Не удалось загрузить регистрации." message={list.error.message} onRetry={() => void list.refetch()} />
          ) : list.isLoading || !data ? (
            <TableSkeleton rows={6} cols={6} />
          ) : tab === 'active' ? (
            <ActiveTable
              data={data}
              allEvents={!values.eventId}
              empty={
                values.nomination || values.pay || values.readiness || values.owner || values.from || values.q ? (
                  <>
                    По этим условиям номеров нет.
                    <br />
                    <TextLink onClick={() => set({ nomination: null, pay: null, readiness: null, owner: null, from: null, to: null, q: null })}>
                      Сбросить фильтры
                    </TextLink>
                  </>
                ) : (
                  'Номеров пока нет — они создаются из сделок.'
                )
              }
            />
          ) : tab === 'reserve' ? (
            <ReserveTable rows={data.items} />
          ) : tab === 'declined' ? (
            <DeclinedTable
              rows={data.items}
              onRefund={(r) => setRefund({ id: r.id, who: r.who, status: r.status, figures: { paid: r.paid, toRefund: r.toRefund }, decline: r.decline })}
            />
          ) : (
            <MovedTable rows={data.items} />
          )}
        </Panel>
      ) : tab === 'chrono' ? (
        <ChronoTab eventId={reportEvent} />
      ) : (
        <RepeatTab eventId={reportEvent} />
      )}

      {refund ? <RefundModal reg={refund} onClose={() => setRefund(null)} /> : null}
    </div>
  );
}

function SubCells({ t }: { t: Totals }) {
  return (
    <>
      <td className="r num">{t.teams}</td>
      <td className="r num">{t.participants}</td>
      <td />
      <td className="r num">{money(t.plan)}</td>
      <td className="r num">{money(t.fact)}</td>
    </>
  );
}

function ActiveTable({ data, allEvents, empty }: { data: RegList; allEvents: boolean; empty: React.ReactNode }) {
  const navigate = useNavigate();
  if (!data.groups.length) return <EmptyState>{empty}</EmptyState>;
  return (
    <>
      <table className="tbl reg">
        <thead>
          <tr>
            <th className="c-reg">Номинация · номер</th>
            <th className="r">Команд</th>
            <th className="r">Участников</th>
            <th>Пакеты</th>
            <th className="r">План</th>
            <th className="r">Факт</th>
            {CHECKLIST_COLUMNS.map((c) => (
              <th key={c.key} className="rc">
                {c.label}
              </th>
            ))}
            <th>Готовность</th>
          </tr>
        </thead>
        <tbody>
          {data.groups.map((g) => (
            <Fragment key={g.event.id}>
              {allEvents ? (
                <tr className="trg obj">
                  <td>{g.event.label}</td>
                  <SubCells t={g.totals} />
                  <td colSpan={7} />
                </tr>
              ) : null}
              {g.nominations.map((n) => {
                const full = n.seats.paidSeats >= n.seats.limit;
                return (
                  <Fragment key={n.id}>
                    <tr className="trg">
                      <td>{n.label}</td>
                      <SubCells t={n.totals} />
                      <td colSpan={7}>
                        <span className="gsum">
                          <span className="g2">
                            Оплатили <b className={full ? 'wtx' : ''}>{n.seats.paidSeats}</b>
                          </span>
                          <ProgressBar value={n.seats.limit ? n.seats.paidSeats / n.seats.limit : 1} full={full} />
                          <span className="g3">
                            Свободно <b className={full ? 'wtx' : ''}>{n.seats.free}</b>
                          </span>
                        </span>
                      </td>
                    </tr>
                    {n.items.map((x) => (
                      <tr key={x.id} className="clk" onClick={() => navigate(`/registrations/${x.id}`)}>
                        <td data-col="primary">
                          <div className="nm">{x.who}</div>
                          <div className="sub">
                            {x.soloist ? 'солист · ' : ''}
                            {x.client.shortName}
                            {x.team?.awaitingHead ? <span className="wtx"> · ждём руководителя</span> : null}
                          </div>
                        </td>
                        <td />
                        <td className="r num">{x.liveRows}</td>
                        <td className="muted">
                          {x.packages.map((p) => (
                            <div key={p.name} className="tl nw">
                              {p.name} {p.count}
                            </div>
                          ))}
                        </td>
                        <td className="r num" data-col="secondary">
                          {money(x.billed)}
                        </td>
                        <td className="r num">{x.paid ? money(x.paid) : <span className="dim">0</span>}</td>
                        {CHECKLIST_COLUMNS.map((c) => (
                          <td key={c.key} className="rc">
                            <StatusDot state={x.checklist[c.key]} />
                          </td>
                        ))}
                        <td>
                          <Pill tone={READINESS[x.readiness].tone}>{READINESS[x.readiness].label}</Pill>
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </Fragment>
          ))}
          <tr className="tot">
            <td>Итого</td>
            <SubCells t={data.total} />
            <td colSpan={7} />
          </tr>
        </tbody>
      </table>
      <Legend items={CHECKLIST_LEGEND} />
    </>
  );
}

function ReserveTable({ rows }: { rows: RegRow[] }) {
  const navigate = useNavigate();
  const action = useM3Action();
  if (!rows.length) return <EmptyState>В резерве пусто. Номера попадают сюда, когда оплатившие набрали лимит номинации.</EmptyState>;
  return (
    <>
      <table className="tbl">
        <thead>
          <tr>
            <th>Номер</th>
            <th>Номинация</th>
            <th className="srt">
              Дата регистрации <span className="ar">▲</span>
            </th>
            <th>В резерве с</th>
            <th className="r">Участников</th>
            <th className="c-ra" />
          </tr>
        </thead>
        <tbody>
          {rows.map((x) => (
            <tr key={x.id} className="clk" onClick={() => navigate(`/registrations/${x.id}`)}>
              <td data-col="primary">
                <div className="nm">{x.who}</div>
                <div className="sub">
                  {x.client.shortName} · {x.event.label}
                </div>
              </td>
              <td className="muted">{x.nomination.name}</td>
              <td className="num">{ruDate(x.createdAt.slice(0, 10))}</td>
              <td className="num">{x.reserveSince ? ruDate(x.reserveSince) : '—'}</td>
              <td className="r num">{x.liveRows}</td>
              <td className="r" data-col="act">
                {x.canPromote ? (
                  <Button
                    small
                    disabled={action.pending}
                    onClick={(e) => {
                      e.stopPropagation();
                      action.run(() => api.post(`/registrations/${x.id}/promote`), `«${x.who}» поднята из резерва, счёт выставлен`);
                    }}
                  >
                    Поднять
                  </Button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Note>Резерв не занимает места и счёта не получает. Поднимает человек — любого, не обязательно первого; порядок по дате регистрации.</Note>
    </>
  );
}

function DeclinedTable({ rows, onRefund }: { rows: RegRow[]; onRefund: (r: RegRow) => void }) {
  const navigate = useNavigate();
  if (!rows.length) return <EmptyState>Отказов нет.</EmptyState>;
  return (
    <table className="tbl">
      <thead>
        <tr>
          <th>Номер</th>
          <th>Номинация</th>
          <th>Причина</th>
          <th>Оформил</th>
          <th className="r">К возврату</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {rows.map((x) => (
          <tr key={x.id} className="clk" onClick={() => navigate(`/registrations/${x.id}`)}>
            <td data-col="primary">
              <div className="nm">{x.who}</div>
              <div className="sub">
                {x.client.shortName} · {x.event.label}
              </div>
            </td>
            <td className="muted">{x.nomination.name}</td>
            <td>{x.decline?.reason}</td>
            <td>
              <div>{x.decline?.by ?? '—'}</div>
              <div className="sub">{x.decline ? ruDate(x.decline.at) : ''}</div>
            </td>
            <td className="r" data-col="secondary">
              {x.toRefund ? <span className="num">{money(x.toRefund)}</span> : <span className="na">—</span>}
            </td>
            <td className="r" data-col="act">
              {x.toRefund ? (
                <Button
                  small
                  onClick={(e) => {
                    e.stopPropagation();
                    onRefund(x);
                  }}
                >
                  Отметить возврат
                </Button>
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function MovedTable({ rows }: { rows: RegRow[] }) {
  const navigate = useNavigate();
  if (!rows.length) return <EmptyState>Переносов нет. Перенос оформляется в карточке номера и не считается отказом.</EmptyState>;
  return (
    <>
      <table className="tbl">
        <thead>
          <tr>
            <th>Номер</th>
            <th>Откуда</th>
            <th>Куда</th>
            <th>Когда</th>
            <th className="r">Зачтено</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((x) => (
            <tr key={x.id} className="clk" onClick={() => navigate(`/registrations/${x.movedTo?.id ?? x.id}`)}>
              <td data-col="primary">
                <div className="nm">{x.who}</div>
                <div className="sub">{x.client.shortName}</div>
              </td>
              <td>
                <div>{x.event.label}</div>
                <div className="sub">{x.nomination.name}</div>
              </td>
              <td>
                {x.movedTo ? (
                  <>
                    <div>{x.movedTo.event}</div>
                    <div className="sub">
                      {x.movedTo.nomination} · {REG_STATUS[x.movedTo.status].label.toLowerCase()}
                    </div>
                  </>
                ) : (
                  <span className="na">—</span>
                )}
              </td>
              <td className="num">{x.movedAt ? ruDate(x.movedAt) : '—'}</td>
              <td className="r num" data-col="secondary">
                {money(x.carried)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Note>
        Перенос — не потеря: место в исходной номинации освободилось, деньги переведены через возврат и платёж «зачёт
        с переноса», история связана в обе стороны.
      </Note>
    </>
  );
}

function ChronoTab({ eventId }: { eventId: string | null }) {
  const navigate = useNavigate();
  const chrono = useChronometry(eventId);
  if (!eventId) return <EmptyState>Номеров пока нет — хронометраж появится с первой регистрацией.</EmptyState>;
  if (chrono.error) return <ErrorState message={chrono.error.message} onRetry={() => void chrono.refetch()} />;
  if (chrono.isLoading || !chrono.data) return <TableSkeleton rows={5} cols={6} />;
  const c = chrono.data;
  if (!c.blocks.length) return <EmptyState>В программе мероприятия нет блоков с номинациями — хронометраж считается по программе.</EmptyState>;
  const approx = (n: number) => (n ? '≈ ' : '');
  return (
    <>
      <MetricRow
        variant="slice"
        items={[
          ...c.blocks.map((b) => ({
            label: b.name,
            value: `${approx(b.estimatedCount)}${mmss(b.durationSec)}`,
            sub: `${nums(b.count)}${b.estimatedCount ? ` · по оценке ${b.estimatedCount}` : ''}`,
          })),
          { label: 'Всего', value: `${approx(c.total.estimatedCount)}${mmss(c.total.durationSec)}`, sub: nums(c.total.count) },
          { label: 'Превышение лимита', value: c.total.overCount, sub: 'номеров', over: c.total.overCount > 0 },
        ]}
      />
      <div className="stack chrono">
        {c.blocks.map((b) => (
          <Panel
            key={b.id}
            flush
            title={b.name}
            count={b.count}
            actions={
              <span className="muted">
                {approx(b.estimatedCount)}
                {mmss(b.durationSec)} из плана {mmss(b.planSec)}
              </span>
            }
          >
            <table className="tbl reg">
              <thead>
                <tr>
                  <th>Номинация · номер</th>
                  <th>Начало</th>
                  <th className="r">Номеров</th>
                  <th className="r">Длительность</th>
                  <th>Лимит</th>
                  <th>Музыка</th>
                </tr>
              </thead>
              <tbody>
                {b.nominations.map((n) => (
                  <Fragment key={n.id}>
                    {/* итог группы — в ячейках под числовыми колонками (кит 2.15) */}
                    <tr className="trg">
                      <td colSpan={2}>
                        {n.label}
                        {n.estimatedCount ? ` · по оценке ${n.estimatedCount}` : ''}
                      </td>
                      <td className="r">{n.items.length}</td>
                      <td className="r">
                        {approx(n.estimatedCount)}
                        {mmss(n.durationSec)}
                      </td>
                      <td colSpan={2} />
                    </tr>
                    {n.items.length ? (
                      n.items.map((x) => (
                        <tr key={x.id} className="clk" onClick={() => navigate(`/registrations/${x.id}`)}>
                          <td data-col="primary">
                            <span className="nm">{x.who}</span>
                          </td>
                          <td className="muted">{n.start ?? <Na />}</td>
                          <td className="r num">1</td>
                          <td className="r num">{x.sec !== null ? mmss(x.sec) : <span className="dim">≈ {mmss(n.avgSec)}</span>}</td>
                          <td className="muted">{mmss(n.limitSec)}</td>
                          <td>
                            {x.sec === null ? (
                              <span className="muted">нет файла</span>
                            ) : x.overSec > 0 ? (
                              <span className="ctx">
                                +{x.overSec} с · штраф {(x.overSec * 0.1).toFixed(1).replace('.', ',')}
                              </span>
                            ) : x.confirmed ? (
                              'принята'
                            ) : (
                              <span className="wtx">на подтверждении</span>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="dim">
                          номеров нет
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </Panel>
        ))}
        <Note>
          Длительность со знаком ≈ — оценка по среднему времени номинации, пока фонограмма не внесена. Учитываются только
          номера «участвует». План блока — среднее время × лимит номинаций (M1).
        </Note>
      </div>
    </>
  );
}

function RepeatTab({ eventId }: { eventId: string | null }) {
  const navigate = useNavigate();
  const rep = useRepeat(eventId);
  if (!eventId) return <EmptyState>Номеров пока нет.</EmptyState>;
  return (
    <Panel flush>
      {rep.error ? (
        <ErrorState message={rep.error.message} onRetry={() => void rep.refetch()} />
      ) : rep.isLoading || !rep.data ? (
        <TableSkeleton rows={5} cols={5} />
      ) : rep.data.people.length ? (
        <>
          <table className="tbl reg">
            <thead>
              <tr>
                <th>Участник · номер</th>
                <th>Номинация</th>
                <th>Пакет</th>
                <th className="r">Цена</th>
                <th>Проверка</th>
              </tr>
            </thead>
            <tbody>
              {rep.data.people.map((p, i) => (
                <Fragment key={`${i}|${p.name}|${p.birthDate}`}>
                  <tr className="trg obj">
                    <td colSpan={5}>
                      {p.name}
                      {p.birthDate ? ` · ${ruDate(p.birthDate)}` : ''}
                      <span className="c">{nums(p.items.length)}</span>
                      {p.possible ? <span className="wtx"> · возможное совпадение</span> : null}
                    </td>
                  </tr>
                  {p.items.map((o) => (
                    <tr key={`${o.registrationId}:${o.row}`} className="clk" onClick={() => navigate(`/registrations/${o.registrationId}`)}>
                      <td data-col="primary">
                        <span className="nm">{o.who}</span>
                        <div className="sub">
                          строка {o.row}
                          {o.first ? ' · первый номер' : ''}
                        </div>
                      </td>
                      <td className="muted">{o.nomination}</td>
                      <td>{o.package.name}</td>
                      <td className="r num">{money(o.price)}</td>
                      <td>
                        {o.expectsExtra ? (
                          <span className="wtx">ожидается дополнительный пакет</span>
                        ) : o.first ? (
                          <span className="dim">основной пакет</span>
                        ) : (
                          'повторное участие'
                        )}
                      </td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
          <Note>
            Совпадение — по клиенту из базы или по ФИО и дате рождения. Первый номер — по дате регистрации. Пакет «Повторное
            участие» ставит менеджер, автоматики нет.
          </Note>
        </>
      ) : (
        <EmptyState>На этом мероприятии никто не стоит в двух номерах.</EmptyState>
      )}
    </Panel>
  );
}
